<template>
  <section class="cart">
    <header class="cart__header">
      <headline :level="embedded ? 2 : 1">
        {{ $t('title') }}
      </headline>
      <c-cart-timer class="cart__timer" />
    </header>

    <transition-group
      name="transition-fade"
      tag="div"
    >
      <div
        v-if="$store.state.cartLoading"
        key="loading"
        class="cart-overview__loading"
      >
        <loader-spinner :size="1.5" />
      </div>

      <template v-else-if="$store.state.cart.total">
        <div
          id="cart"
          key="content"
          class="cart__content"
        >
          <div
            v-if="$store.state.cart.tickets.total_articles"
            class="cart__ticket"
          >
            <h3 class="cart__subtitle">
              {{ $t($store.state.cart.tickets.gap === GAP_GIFT_CARD ? 'giftcards' : 'admission') }}
            </h3>

            <div
              v-if="$store.state.cart.tickets.gap === GAP_GIFT_CARD"
              class="cart__datetime"
            >
              <span class="cart__date">
                {{ $t('selectedGiftCardTemplate') }}:
                <strong>{{ languageVariant($store.state.selectedGiftCardTemplate, 'title') }}</strong>
              </span>
            </div>

            <div
              v-else
              class="cart__datetime"
            >
              <span class="cart__date">
                {{ $t('selectedDate') }}:
                <strong>{{ humanDate($store.state.cart.tickets.date) }}</strong>
              </span>
              <span class="cart__time">
                {{ $t('arrivalSlot') }}:
                <strong>{{ ticketsGapTime }}</strong>
              </span>
            </div>

            <section class="cart-table">
              <header class="cart-table__row cart-table__row--header">
                <span class="cart-table__quantity">
                  <small>{{ $t('table.quantity') }}</small>
                </span>
                <span class="cart-table__type">
                  <small>{{ $t('table.type') }}</small>
                </span>
                <span class="cart-table__price">
                  <small>{{ $t('table.price.unit') }}</small>
                </span>
                <span class="cart-table__price">
                  <small>{{ $t('table.price.total') }}</small>
                </span>
              </header>

              <div
                v-for="(article, typeId) in $store.state.cart.tickets.articles"
                :key="typeId"
                class="cart-table__row"
              >
                <span class="cart-table__quantity-type-row">
                  <span class="cart-table__quantity cart-table__quantity--count">
                    {{ article.count }}
                  </span>
                  <span class="cart-table__type">
                    {{ articleTypes[$i18n.locale][typeId] }}
                  </span>
                </span>
                <span class="cart-table__price">
                  <small class="cart-table__price-info">
                    {{ $t('table.price.unit') }}
                  </small>
                  <span class="cart-table__price-number">
                    {{ humanPrice(article.discounted_price) }}&nbsp;€
                  </span>
                </span>
                <span class="cart-table__price">
                  <small class="cart-table__price-info">
                    {{ $t('table.price.total') }}
                  </small>
                  <span class="cart-table__price-number">
                    {{ humanPrice(article.discounted_price * article.count) }}&nbsp;€
                  </span>
                </span>
              </div>

              <div class="cart-table__row">
                <span class="cart-table__price">
                  <small>{{ $t('table.total') }}</small>
                </span>
                <span class="cart-table__price">
                  {{ humanPrice($store.state.cart.tickets.subtotal) }}&nbsp;€
                </span>
              </div>

              <footer
                v-if="$store.state.cart.tickets.fee_total"
                class="cart-table__row cart-table__row--fees"
              >
                <span>
                  <small v-html="$t('fee')" />
                </span>
                <span class="cart-table__price">
                  <small>{{ humanPrice($store.state.cart.tickets.fee_total) }}&nbsp;€</small>
                </span>
              </footer>
            </section>
          </div>

          <div
            v-if="tours.articleAmount"
            class="cart__ticket"
          >
            <h3 class="cart__subtitle">
              {{ $t('tours') }}
            </h3>

            <div class="cart__datetime">
              <span class="cart__date">
                {{ $t('selectedDate') }}:
                <strong>{{ humanDate(tours.date) }}</strong>
              </span>
            </div>

            <section class="cart-table">
              <header class="cart-table__row cart-table__row--header">
                <span class="cart-table__quantity">
                  <small>{{ $t('table.quantity') }}</small>
                </span>
                <span class="cart-table__type">
                  <small>{{ $t('table.type') }}</small>
                </span>
                <span class="cart-table__price">
                  <small>{{ $t('table.price.unit') }}</small>
                </span>
                <span class="cart-table__price">
                  <small>{{ $t('table.price.total') }}</small>
                </span>
              </header>

              <div
                v-for="(group, time) in tours.groupedArticles"
                :key="time"
              >
                <h4 class="cart-table__group-info">
                  <span class="text-quotes">{{ group.name }}</span>, {{ $t('tourStart') }}: {{ time }}
                </h4>

                <div
                  v-for="article in group.articles"
                  :key="article.id"
                  class="cart-table__row"
                >
                  <span class="cart-table__quantity-type-row">
                    <span class="cart-table__quantity cart-table__quantity--count">
                      {{ article.count }}
                    </span>
                    <span class="cart-table__type">
                      {{ articleTypes[$i18n.locale][article.id] }}
                    </span>
                  </span>
                  <span class="cart-table__price">
                    <small class="cart-table__price-info">
                      {{ $t('table.price.unit') }}
                    </small>
                    <span class="cart-table__price-number">
                      {{ humanPrice(article.price) }}&nbsp;€
                    </span>
                  </span>
                  <span class="cart-table__price">
                    <small class="cart-table__price-info">
                      {{ $t('table.price.total') }}
                    </small>
                    <span class="cart-table__price-number">
                      {{ humanPrice(article.subtotal) }}&nbsp;€
                    </span>
                  </span>
                </div>
              </div>

              <div class="cart-table__row">
                <span class="cart-table__price">
                  <small>{{ $t('table.total') }}</small>
                </span>
                <span class="cart-table__price">
                  {{ humanPrice(tours.subtotal) }}&nbsp;€
                </span>
              </div>

              <footer
                v-if="tours.fee"
                class="cart-table__row cart-table__row--fees"
              >
                <span>
                  <small v-html="$t('fee')" />
                </span>
                <span class="cart-table__price">
                  <small>{{ humanPrice(tours.fee) }}&nbsp;€</small>
                </span>
              </footer>
            </section>
          </div>

          <div
            v-if="$store.state.cart.events.total_articles"
            class="cart__ticket"
          >
            <h3 class="cart__subtitle">
              {{ $t($store.state.cart.events.gap === GAP_GIFT_CARD ? 'giftcards' : 'events') }}
            </h3>

            <div
              v-if="$store.state.cart.events.gap === GAP_GIFT_CARD"
              class="cart__datetime"
            >
              <span class="cart__date">
                {{ $t('selectedGiftCardTemplate') }}:
                <strong>{{ languageVariant($store.state.selectedGiftCardTemplate, 'title') }}</strong>
              </span>
            </div>

            <div
              v-else
              class="cart__datetime"
            >
              <span class="cart__date">
                {{ $t('selectedDate') }}:
                <strong>{{ humanDate($store.state.cart.events.date) }}</strong>
              </span>
              <span class="cart__time">
                {{ $t('arrivalEvent') }}:
                <strong>{{ $store.state.selectedEventTimes.from }} - {{ $store.state.selectedEventTimes.until }}</strong>
              </span>
            </div>

            <section class="cart-table">
              <header class="cart-table__row cart-table__row--header">
                <span class="cart-table__quantity">
                  <small>{{ $t('table.quantity') }}</small>
                </span>
                <span class="cart-table__type">
                  <small>{{ $t('table.type') }}</small>
                </span>
                <span class="cart-table__price">
                  <small>{{ $t('table.price.unit') }}</small>
                </span>
                <span class="cart-table__price">
                  <small>{{ $t('table.price.total') }}</small>
                </span>
              </header>

              <div
                v-for="(article, typeId) in $store.state.cart.events.articles"
                :key="typeId"
                class="cart-table__row"
              >
                <span class="cart-table__quantity-type-row">
                  <span class="cart-table__quantity cart-table__quantity--count">
                    {{ article.count }}
                  </span>
                  <span class="cart-table__type">
                    {{ articleTypes[$i18n.locale][typeId] }}
                  </span>
                </span>
                <span class="cart-table__price">
                  <small class="cart-table__price-info">
                    {{ $t('table.price.unit') }}
                  </small>
                  <span class="cart-table__price-number">
                    {{ humanPrice(article.price) }}&nbsp;€
                  </span>
                </span>
                <span class="cart-table__price">
                  <small class="cart-table__price-info">
                    {{ $t('table.price.total') }}
                  </small>
                  <span class="cart-table__price-number">
                    {{ humanPrice(article.price * article.count) }}&nbsp;€
                  </span>
                </span>
              </div>

              <div class="cart-table__row">
                <span class="cart-table__price">
                  <small>{{ $t('table.total') }}</small>
                </span>
                <span class="cart-table__price">
                  {{ humanPrice($store.state.cart.events.subtotal) }}&nbsp;€
                </span>
              </div>

              <footer
                v-if="$store.state.cart.events.fee_total"
                class="cart-table__row cart-table__row--fees"
              >
                <span>
                  <small v-html="$t('fee')" />
                </span>
                <span class="cart-table__price">
                  <small>{{ humanPrice($store.state.cart.events.fee_total) }}&nbsp;€</small>
                </span>
              </footer>
            </section>
          </div>

          <div
            v-if="$store.state.cart.vouchers.total_articles"
            class="cart__ticket"
          >
            <h3 class="cart__subtitle">
              {{ $t('vouchers.title') }}
            </h3>

            <div
              v-if="!isGiftCardOrder"
              class="cart__datetime"
            >
              <p class="cart__warning">
                {{ $t('vouchers.hint') }}
              </p>
            </div>

            <section class="cart-table">
              <header class="cart-table__row cart-table__row--header">
                <span class="cart-table__quantity">
                  <small>{{ $t('table.quantity') }}</small>
                </span>
                <span class="cart-table__type">
                  <small>{{ $t('vouchers.type') }}</small>
                </span>
                <span class="cart-table__price">
                  <small>{{ $t('table.price.unit') }}</small>
                </span>
                <span class="cart-table__price">
                  <small>{{ $t('table.price.total') }}</small>
                </span>
              </header>

              <div
                v-for="(article, voucherId) in $store.state.cart.vouchers.articles"
                :key="voucherId"
                class="cart-table__row"
              >
                <span class="cart-table__quantity-type-row">
                  <span class="cart-table__quantity cart-table__quantity--count">
                    <button
                      class="cart-overview__delete"
                      @click="removeVouchers(voucherId)"
                    >
                      <icon type="delete" />
                    </button>
                    {{ article.count }}
                  </span>
                  <span class="cart-table__type">
                    {{ voucherTitle(voucherId) }}
                  </span>
                </span>
                <span class="cart-table__price">
                  <small class="cart-table__price-info">
                    {{ $t('table.price.unit') }}
                  </small>
                  <span class="cart-table__price-number">
                    {{ humanPrice(article.price) }}&nbsp;€
                  </span>
                </span>
                <span class="cart-table__price">
                  <small class="cart-table__price-info">
                    {{ $t('table.price.total') }}
                  </small>
                  <span class="cart-table__price-number">
                    {{ humanPrice(article.price * article.count) }}&nbsp;€
                  </span>
                </span>
              </div>

              <div class="cart-table__row">
                <span class="cart-table__price">
                  <small>{{ $t('table.total') }}</small>
                </span>
                <span class="cart-table__price">
                  {{ humanPrice($store.state.cart.vouchers.total) }}&nbsp;€
                </span>
              </div>
            </section>
          </div>

          <div class="cart-table__grand-total">
            {{ $t('grandTotal') }}
            <span class="cart-table__grand-total-amount">
              <strong>{{ humanPrice($store.state.cart.total) }} €</strong>
            </span>
          </div>
        </div>

        <footer
          key="footer"
          class="cart__footer"
        >
          <button-group align="center">
            <action-link
              :router-link="backToLink"
              exact
            >
              <icon
                type="chevron-left"
                :size="0.75"
              />
              {{ $t('backToBooking') }}
            </action-link>

            <action
              v-if="!embedded"
              color="primary"
              @click="scrollToRecommendations"
            >
              <span v-html="$t('recommendations')" />
              <icon
                type="expand"
                :size="0.75"
              />
            </action>

            <action-link
              v-if="!embedded && cartValidation.valid"
              :router-link="{ name: `booking-personal-information-${$i18n.locale}` }"
            >
              <span v-html="$t('completeBooking')" />
              <icon
                type="chevron-right"
                :size="0.75"
              />
            </action-link>

            <p
              v-else
              class="cart__error"
            >
              {{ $t(cartValidation.error, cartValidation.errorArgs) }}
            </p>
          </button-group>
        </footer>
      </template>

      <template v-else>
        <div
          key="content"
          class="cart__content"
        >
          <p class="cart__note">
            {{ $t('emptyCart') }}
          </p>
        </div>

        <footer
          key="footer"
          class="cart__footer"
        >
          <action-link
            :router-link="{ name: `booking-${$i18n.locale}` }"
            color="primary"
            exact
          >
            <icon
              type="chevron-left"
              :size="0.75"
            />
            {{ $t('ticketBooking') }}
          </action-link>
        </footer>
      </template>
    </transition-group>
  </section>
</template>

<script>
import action from './action.vue'
import actionLink from './actionLink.vue'
import buttonGroup from './buttonGroup.vue'
import CCartTimer from './CCartTimer.vue'
import headline from './headline.vue'
import icon from './icon.vue'
import loaderSpinner from './loaderSpinner.vue'

import cartMixin from '../mixins/cart.js'
import cartValidationMixin from '../mixins/cartValidation.js'

import { GAP_GIFT_CARD } from '../settings.js'

export default {
  name: 'CCartBooking',

  components: {
    action,
    actionLink,
    buttonGroup,
    CCartTimer,
    headline,
    icon,
    loaderSpinner
  },

  mixins: [cartMixin, cartValidationMixin],

  props: {
    embedded: {
      type: Boolean
    }
  },

  computed: {
    GAP_GIFT_CARD: () => GAP_GIFT_CARD,
    isGiftCardOrder () {
      if (this.$store.state.cart.tickets.gap === GAP_GIFT_CARD) {
        return true
      }

      if (this.$store.state.cart.events.gap === GAP_GIFT_CARD) {
        return true
      }

      return false
    },
    backToLink () {
      if (this.isGiftCardOrder) {
        return { name: `giftcards-${this.$i18n.locale}` }
      }

      if (this.hasYullbe) {
        return { name: 'yullbe' }
      }

      if (this.tours.articleAmount) {
        return { name: `booking-tours-${this.$i18n.locale}` }
      }

      return { name: `booking-${this.$i18n.locale}` }
    }
  },

  created () {
    const dontResetSelection = !Number.isNaN(this.$route.params.people)
    this.$store.dispatch('getCart', { dontResetSelection })

    // fetch tour config for cart validation
    this.$store.dispatch('getToursConfig', {})
  },

  methods: {
    voucherTitle: function (voucherId) {
      const vouchers = this.$store.state.vouchers

      for (const voucherGroup in vouchers) {
        for (const voucherIndex in vouchers[voucherGroup]) {
          if (vouchers[voucherGroup][voucherIndex].id === parseInt(voucherId)) {
            return this.$i18n.locale === 'de'
              ? vouchers[voucherGroup][voucherIndex].text
              : vouchers[voucherGroup][voucherIndex].text_en
          }
        }
      }

      return ''
    },

    removeVouchers (voucherId) {
      this.$store.dispatch('removeVouchersFromCart', voucherId)
    },

    languageVariant (row, field) {
      if (!row) {
        return ''
      }

      const languageField = field + '_' + this.$i18n.locale

      if (languageField in row) {
        return row[languageField]
      }

      return ''
    }
  }
}
</script>

<i18n locale="en">
{
  "title": "Booking summary",
  "emptyCart": "Your shopping cart is empty!",
  "admission": "Tickets Miniatur Wunderland Hamburg",
  "tours": "Experiences Miniatur Wunderland Hamburg",
  "tourStart": "Start",
  "giftcards": "Gift voucher Miniatur Wunderland Hamburg",
  "events": "Special events",
  "selectedDate": "Chosen date",
  "arrivalSlot": "Time slot of arrival",
  "arrivalEvent": "Event time",
  "selectedGiftCardTemplate": "Selected template for gift voucher",
  "table": {
    "quantity": "Quantity",
    "type": "Type of ticket",
    "price": {
      "unit": "Price",
      "total": "Subtotal"
    },
    "total": "Total"
  },
  "fee": "plus system fees 0.50&nbsp;€ per ticket",
  "grandTotal": "Grand total",
  "backToBooking": "Change order",
  "recommendations": "Continue to our <strong class=\"no-hyphenation\">recommendations</strong>",
  "ticketBooking": "Ticket booking",
  "completeBooking": "Complete <strong>Booking Now</strong>",
  "vouchers": {
    "title": "Vouchers",
    "type": "Type of voucher",
    "hint": "Attention: Please pick up your vouchers at the Miniatur Wunderland."
  }
}
</i18n>

<i18n locale="de">
{
  "title": "Buchungsübersicht",
  "emptyCart": "Sie haben keine Artikel im Warenkorb!",
  "admission": "Eintrittskarten Miniatur Wunderland Hamburg",
  "tours": "Erlebnisse Miniatur Wunderland Hamburg",
  "tourStart": "Beginn",
  "giftcards": "Geschenkgutscheine Miniatur Wunderland Hamburg",
  "events": "Sonderveranstaltungen",
  "selectedDate": "Gewähltes Datum",
  "arrivalSlot": "Einlasszeit",
  "arrivalEvent": "Veranstaltungszeit",
  "selectedGiftCardTemplate": "Gewählte Vorlage",
  "table": {
    "quantity": "Anzahl",
    "type": "Ticketart",
    "price": {
      "unit": "Einzelpreis",
      "total": "Gesamtpreis"
    },
    "total": "Summe"
  },
  "fee": "zzgl. Systemgebühren 0,50&nbsp;€ je Ticket",
  "grandTotal": "Gesamtsumme",
  "backToBooking": "Bestellung ändern",
  "recommendations": "Weiter zu unseren <strong class=\"no-hyphenation\">Empfehlungen</strong>",
  "ticketBooking": "Zur Ticketbuchung",
  "completeBooking": "<strong>Buchung jetzt</strong> abschließen",
  "vouchers": {
    "title": "Gutscheine",
    "type": "Gutscheinart",
    "hint": "Achtung: Bitte holen Sie die Gutscheine vorher im Miniatur Wunderland ab."
  }
}
</i18n>
